import React, { useMemo } from 'react';
import { Box, LinearProgress, Typography } from '@mui/material';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import { SidebarPanel } from '@components/content-side-bar/SidebarPanel';
import { SidebarButton } from '@components/content-side-bar/SidebarButton';
import { PositionChangeIndicator } from '@pages/Portal/Competitions/components/RankingTable/components/PositionChangeIndicator';
import { getCompetitionDurationProgress } from '@pages/Portal/Competitions/helpers';
import { useTranslation } from 'react-i18next';
import { IYourRankingSidebarPanel } from './interfaces';

export const YourRankingSidebarPanel: React.FC<IYourRankingSidebarPanel> = ({
  rankingEntry,
  participantCount,
  minCountingScores,
  startDate,
  endDate,
  onViewRanking,
}): JSX.Element => {
  const { t } = useTranslation();

  const competitionProgress = useMemo(
    () => getCompetitionDurationProgress(startDate, endDate),
    [endDate, startDate]
  );

  return (
    <SidebarPanel title={t('competitions:portal.details.yourRanking.title')}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 1,
          mb: 1.5,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {rankingEntry && rankingEntry.rank !== null ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'baseline',
                lineHeight: 1.1,
              }}
            >
              <Typography
                variant="h3"
                component="span"
                fontWeight="bold"
                sx={{ fontSize: { xs: '2.25rem', sm: '2.5rem' } }}
              >
                {rankingEntry.rank}
              </Typography>
              <Typography
                component="span"
                color="text.secondary"
                fontWeight="medium"
                sx={{
                  fontSize: { xs: '1.125rem', sm: '1.375rem' },
                  ml: 0.5,
                }}
              >
                {' / '}
                {participantCount}
              </Typography>
            </Box>
          ) : rankingEntry && minCountingScores !== undefined ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'baseline',
                lineHeight: 1.1,
              }}
            >
              <Typography
                variant="h3"
                component="span"
                fontWeight="bold"
                sx={{ fontSize: { xs: '2.25rem', sm: '2.5rem' } }}
              >
                {rankingEntry.submissionCount}
              </Typography>
              <Typography
                component="span"
                color="text.secondary"
                fontWeight="medium"
                sx={{
                  fontSize: { xs: '1.125rem', sm: '1.375rem' },
                  ml: 0.5,
                }}
              >
                {' / '}
                {minCountingScores}
              </Typography>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t('competitions:portal.details.yourRanking.noPositionYet')}
            </Typography>
          )}
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            pt: 0.5,
            color: 'text.secondary',
          }}
        >
          <EmojiEventsOutlinedIcon fontSize="small" sx={{ mb: 0.5 }} />
          {rankingEntry && rankingEntry.rank !== null && (
            <PositionChangeIndicator
              rank={rankingEntry.rank}
              previousRank={rankingEntry.previousRank}
              positionChange={rankingEntry.positionChange}
            />
          )}
        </Box>
      </Box>

      <LinearProgress
        variant="determinate"
        value={competitionProgress}
        sx={{
          height: 6,
          borderRadius: 3,
          bgcolor: 'grey.200',
          '& .MuiLinearProgress-bar': {
            borderRadius: 3,
          },
        }}
      />

      {onViewRanking && (
        <SidebarButton onClick={onViewRanking} sx={{ mt: 1.5 }}>
          {t('competitions:portal.details.yourRanking.viewRanking')}
        </SidebarButton>
      )}
    </SidebarPanel>
  );
};
